'use client'

import { useMemo } from 'react'

import { resolveDocumentationSlug } from '@/lib/github/documentationSlug'
import type { CategoryNode, TutorialSection } from '@/lib/github/types'
import { DOCS_ROUTES } from '@/shared/docs-shared.const'
import { DOCUMENTATION_LAYOUT_SHELL_TEST_IDS } from '@/shared/docs-test-ids.const'
import { useCurrentSectionPath, useSectionBreadcrumbs } from '@/shared/hooks'

import { AcademyLayout } from '../AcademyLayout'
import { collectAvailableLanguages } from '../QuickStartLayoutShell/utils/collectAvailableLanguages'

import { DocumentationLanguageContext } from './context/documentationLanguageContext'
import { useDocumentationLanguage } from './hooks/useDocumentationLanguage'

interface DocumentationLayoutShellProps {
  sections: TutorialSection[]
  categoryTree: CategoryNode[]
  rootArticles: TutorialSection[]
  children: React.ReactNode
}

export function DocumentationLayoutShell({
  sections,
  categoryTree,
  rootArticles,
  children,
}: DocumentationLayoutShellProps) {
  const currentSectionPath = useCurrentSectionPath({
    routePrefix: DOCS_ROUTES.documentation,
    sections,
    slugToPath: (slugParts) => resolveDocumentationSlug(slugParts, sections)?.articlePath,
  })

  const breadcrumbs = useSectionBreadcrumbs(sections, currentSectionPath)

  const availableLanguages = useMemo(() => collectAvailableLanguages(sections), [sections])
  const { selectedLanguage, setSelectedLanguage } = useDocumentationLanguage(availableLanguages)

  const languageContextValue = useMemo(
    () => ({
      sections,
      selectedLanguage,
      setSelectedLanguage,
      availableLanguages,
      routePrefix: DOCS_ROUTES.documentation,
    }),
    [sections, selectedLanguage, setSelectedLanguage, availableLanguages]
  )

  return (
    <DocumentationLanguageContext data-testid={DOCUMENTATION_LAYOUT_SHELL_TEST_IDS.root} value={languageContextValue}>
      <AcademyLayout
        tutorialSections={sections}
        currentTutorialPath={currentSectionPath}
        breadcrumbs={breadcrumbs}
        categoryTree={categoryTree}
        rootArticles={rootArticles}
      >
        {children}
      </AcademyLayout>
    </DocumentationLanguageContext>
  )
}
